import useAuth from '@app/auth/use-auth';
import { IntercomArticle } from '@app/services/intercom/content';
import {
  CompanyReportCategory,
  CompanyReportCategoryStatusStatusEnum,
  ScopeEnum,
} from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import { useMemo } from 'react';
import { generatePath, useParams } from 'react-router-dom';
import { ccfRoutes } from '../ccf-routes';
import { useCCF } from './ccf-context';
import { reportSlugs, ScopeCategorySlug } from './slugs';

const SECR_SCOPE3_SLUGS: ScopeCategorySlug[] = [reportSlugs.businessTravel];

export type Scope3Category = 'purchased-goods' | 'supply-chain-logistics';

export type ScopeCategory = {
  scope: ScopeEnum;
  scope3Category?: Scope3Category;
  name: string;
  slug: ScopeCategorySlug;
  description: string;
  href: string;
  icon: React.ReactNode;
  supportArticle?: IntercomArticle;
  totalEmissions?: number;
  isSupported?: boolean;
  status?: CompanyReportCategoryStatusStatusEnum;
  hasActivityData?: boolean;
  included?: boolean;
  disabled?: boolean;
  uuid?: string;
  index?: number;
};

const includedStatuses: CompanyReportCategoryStatusStatusEnum[] = [
  CompanyReportCategoryStatusStatusEnum.included,
  CompanyReportCategoryStatusStatusEnum.complete,
  CompanyReportCategoryStatusStatusEnum.no_emissions,
];

export function useScopeCategories(): ScopeCategory[] {
  const { report_uuid: reportUuid } = useParams();

  return [
    {
      scope: '1',
      name: 'Stationary combustion',
      slug: reportSlugs.stationaryCombustion,
      description: 'Fuels combusted directly on your sites',
      href: generatePath(ccfRoutes.STATIONARY_COMBUSTION, {
        report_uuid: reportUuid,
      }),
      icon: <Icons.flame className="text-orange-400" size={20} />,
      supportArticle: 'stationaryCombustion',
    },
    {
      scope: '1',
      name: 'Mobile combustion',
      slug: reportSlugs.mobileCombustion,
      description: 'Fuels combusted in your vehicles',
      href: generatePath(ccfRoutes.MOBILE_COMBUSTION, {
        report_uuid: reportUuid,
      }),
      icon: <Icons.truck className="text-orange-400" size={20} />,
      supportArticle: 'mobileCombustion',
    },
    {
      scope: '1',
      name: 'Direct gas emissions',
      slug: reportSlugs.directGasEmissions,
      description: 'From fridges, AC, extinguishers and fermentation',
      href: generatePath(ccfRoutes.DIRECT_GAS, {
        report_uuid: reportUuid,
      }),
      icon: <Icons.refrigerator className="text-orange-400" size={20} />,
      supportArticle: 'directGas',
    },

    {
      scope: '2',
      name: 'Electricity',
      slug: reportSlugs.electricity,
      description: 'Electricity consumed across your sites',
      href: generatePath(ccfRoutes.ELECTRICITY, {
        report_uuid: reportUuid,
      }),
      icon: <Icons.zap className="text-orange-400" size={20} />,
      supportArticle: 'electricity',
    },
    {
      scope: '2',
      name: 'Heating and steam',
      slug: reportSlugs.heat,
      description: 'Heating or steam supplied to you by an external provider',
      href: generatePath(ccfRoutes.HEATING, {
        report_uuid: reportUuid,
      }),
      icon: <Icons.thermometer className="text-orange-400" size={20} />,
      supportArticle: 'heating',
    },
    {
      scope: '2',
      name: 'Cooling',
      slug: reportSlugs.cooling,
      description: 'Cooling supplied to you by an external provider',
      href: generatePath(ccfRoutes.HEATING, {
        report_uuid: reportUuid,
      }),
      icon: <Icons.snowflake className="text-orange-400" size={20} />,
      supportArticle: 'heating',
    },
    {
      scope: '3',
      scope3Category: 'purchased-goods',
      name: 'Food and drink',
      slug: reportSlugs.foodAndDrink,
      description: 'Food and drink you have purchased',
      href: generatePath(ccfRoutes.FOOD, {
        report_uuid: reportUuid,
      }),
      icon: <Icons.carrot className="text-orange-400" size={20} />,
      supportArticle: 'food',
    },
    {
      scope: '3',
      scope3Category: 'purchased-goods',
      name: 'Packaging',
      slug: reportSlugs.packaging,
      description:
        'Items you buy to package, store and transport your products',
      href: generatePath(ccfRoutes.PACKAGING, {
        report_uuid: reportUuid,
      }),
      icon: <Icons.package className="text-orange-400" size={20} />,
      supportArticle: 'packaging',
    },
    {
      scope: '3',
      name: 'Fuel and energy-related activities',
      slug: reportSlugs.fuelAndEnergy,
      description:
        'Upstream emissions generated by your activities in scope 1 and 2',
      href: generatePath(ccfRoutes.HEATING, {
        report_uuid: reportUuid,
      }),
      disabled: true,
      icon: <Icons.arrowUpCircle className="text-orange-400" size={20} />,
    },
    {
      scope: '3',
      name: 'Supply chain logistics',
      slug: reportSlugs.supplyChainLogistics,
      scope3Category: 'supply-chain-logistics',
      description: 'Transport of goods from your suppliers to the end consumer',
      href: generatePath(ccfRoutes.SUPPLY_CHAIN_LOGISTICS, {
        report_uuid: reportUuid,
      }),
      icon: <Icons.truck className="text-orange-400" size={20} />,
    },
    {
      scope: '3',
      name: 'Other purchased goods and services',
      slug: reportSlugs.otherPurchasedGoods,
      scope3Category: 'purchased-goods',
      description:
        'Any goods or services you pay for other than food and packaging',
      href: generatePath(ccfRoutes.OTHER_PURCHASED_GOODS, {
        report_uuid: reportUuid,
      }),
      icon: <Icons.shoppingCart className="text-orange-400" size={20} />,
      supportArticle: 'otherS3Categories',
    },
    {
      scope: '3',
      name: 'Capital goods',
      slug: reportSlugs.capitalGoods,
      description: 'Long-term assets you have purchased',
      href: generatePath(ccfRoutes.CAPITAL_GOODS, {
        report_uuid: reportUuid,
      }),
      icon: <Icons.forklift className="text-orange-400" size={20} />,
      supportArticle: 'otherS3Categories',
    },
    {
      scope: '3',
      name: 'Waste from operations',
      slug: reportSlugs.waste,
      description: 'Treatment and disposal of waste you have generated',
      href: generatePath(ccfRoutes.WASTE, {
        report_uuid: reportUuid,
      }),
      icon: <Icons.recycle className="text-orange-400" size={20} />,
      supportArticle: 'otherS3Categories',
    },
    {
      scope: '3',
      name: 'Business travel',
      slug: reportSlugs.businessTravel,
      description: 'Travel on transport not owned or operated by you',
      href: generatePath(ccfRoutes.BUSINESS_TRAVEL, {
        report_uuid: reportUuid,
      }),
      icon: <Icons.plane className="text-orange-400" size={20} />,
      supportArticle: 'otherS3Categories',
    },
    {
      scope: '3',
      name: 'Employee commuting',
      slug: reportSlugs.employeeCommuting,
      description: 'Employee travel between home and workplace',
      href: generatePath(ccfRoutes.EMPLOYEE_COMMUTING, {
        report_uuid: reportUuid,
      }),
      icon: <Icons.car className="text-orange-400" size={20} />,
      supportArticle: 'otherS3Categories',
    },
    {
      scope: '3',
      name: 'Leased assets - upstream',
      slug: reportSlugs.leasedAssetsUpstream,
      description: 'Assets you lease from others and operate yourself',
      href: generatePath(ccfRoutes.LEASED_ASSETS_UPSTREAM, {
        report_uuid: reportUuid,
      }),
      icon: <Icons.warehouse className="text-orange-400" size={20} />,
      supportArticle: 'otherS3Categories',
    },
    {
      scope: '3',
      name: 'Sold products - processing',
      slug: reportSlugs.soldProductsProcessing,
      description:
        'Further processing of your products before sale to consumer',
      href: generatePath(ccfRoutes.SOLD_PRODUCTS_PROCESSING, {
        report_uuid: reportUuid,
      }),
      icon: <Icons.cookingPot className="text-orange-400" size={20} />,
      supportArticle: 'otherS3Categories',
    },
    {
      scope: '3',
      name: 'Sold products - use',
      slug: reportSlugs.soldProductsUse,
      description: 'Consumer storage or processing of your products',
      href: generatePath(ccfRoutes.SOLD_PRODUCTS_USE, {
        report_uuid: reportUuid,
      }),
      icon: <Icons.microwave className="text-orange-400" size={20} />,
      supportArticle: 'otherS3Categories',
    },
    {
      scope: '3',
      name: 'Sold products - end-of-life',
      slug: reportSlugs.soldProductsEndOfLife,
      description: 'Disposal of your products and their packaging',
      href: generatePath(ccfRoutes.SOLD_PRODUCTS_END_OF_LIFE, {
        report_uuid: reportUuid,
      }),
      icon: <Icons.trash2 className="text-orange-400" size={20} />,
      supportArticle: 'otherS3Categories',
    },
    {
      scope: '3',
      name: 'Leased assets - downstream',
      slug: reportSlugs.leasedAssetsDownstream,
      description: 'Assets you own but lease to others to operate',
      href: generatePath(ccfRoutes.LEASED_ASSETS_DOWNSTREAM, {
        report_uuid: reportUuid,
      }),
      icon: <Icons.refrigerator className="text-orange-400" size={20} />,
      supportArticle: 'otherS3Categories',
    },
    {
      scope: '3',
      name: 'Franchises',
      slug: reportSlugs.franchises,
      description: 'Outlets operating under your brand name',
      href: generatePath(ccfRoutes.FRANCHISES, {
        report_uuid: reportUuid,
      }),
      icon: <Icons.store className="text-orange-400" size={20} />,
      supportArticle: 'otherS3Categories',
    },
    {
      scope: '3',
      name: 'Investments',
      slug: reportSlugs.investments,
      description: 'Companies you support through shares or loans',
      href: generatePath(ccfRoutes.INVESTMENTS, {
        report_uuid: reportUuid,
      }),
      icon: <Icons.pound className="text-orange-400" size={20} />,
      supportArticle: 'otherS3Categories',
    },
  ];
}

export const useProcessScopes = () => {
  const baseCategories = useScopeCategories();
  const { categories, scope3Supported } = useCCF();
  const { user } = useAuth();
  const hasSecrFeature = !!user?.features.secr;

  return useMemo(() => {
    const buckets = baseCategories?.reduce(
      (acc, item) => {
        const category = categories?.find(
          (c: CompanyReportCategory) => c.slug === item.slug
        );

        if (category) {
          item = {
            ...item,
            uuid: category.uuid,
            index: category.index,
            totalEmissions: category.totalEmissions!,
            status: category.status,
            hasActivityData: category.hasActivityData,
            included: includedStatuses.includes(category.status!),
            isSupported: category.isSupported,
          };
        }

        acc['scope' + item.scope].push(item);
        return acc;
      },
      {
        scope1: [],
        scope2: [],
        scope3: [],
      } as Record<string, ScopeCategory[]>
    );

    // When a customer has SECR but not full Scope 3, surface SECR-mandatory
    // categories above the upsell gate.
    const showStandaloneSecr = !scope3Supported && hasSecrFeature;
    const secrCategories = showStandaloneSecr
      ? buckets.scope3.filter((c) => SECR_SCOPE3_SLUGS.includes(c.slug))
      : [];
    const scope3 = showStandaloneSecr
      ? buckets.scope3.filter((c) => !SECR_SCOPE3_SLUGS.includes(c.slug))
      : buckets.scope3;

    return {
      scope1: buckets.scope1,
      scope2: buckets.scope2,
      scope3,
      secrCategories,
    };
  }, [baseCategories, categories, scope3Supported, hasSecrFeature]);
};
